import { boxOf, isAttached, paintOrder, type Box, type El } from './model'

/**
 * A QUESTION ABOUT AN AREA OF THE BOARD (owner, 2026-10-31: 使用者有時候要針對某一個繪製的元件來告訴 Agent 會有困難 … 一個 Chat,
 * 再加上一個小虛線框框的這種圖示 … 選擇某一個架構的區域 … 連同架構或者是 Attach 的圖案一起告訴 Agent … 傳給 Agent 之後呢, 它可以自動
 * 消失, 或者是讓使用者選擇把它隱藏起來, 或者是選擇可以刪除這一個對話框).
 *
 * The person draws a dashed box round part of the board and asks about it. The question goes to the project's chat
 * like any message from the board, carrying the box, the items they saw inside it, and a picture of it (chat
 * messages.ts AreaFields); an agent reads it all with `avengents board <conv> area <seq>`.
 *
 * Afterwards the box leaves the board by itself, unless the person keeps it; a kept one can be hidden (a small pin
 * stays where it was) or deleted. Kept boxes are THIS DEVICE's — the board everyone draws on never holds them, so a
 * question never covers the drawing for anyone else; the message in the chat is what the room shares, and its chip
 * shows the box again on anyone's board (showArea).
 */

/** The most items one area names (the chat service's MAX_AREA_IDS). */
const MAX_AREA_IDS = 200
/** The colour of a question's box: not the selection's blue, so a box and a selection are never mistaken. */
export const ASK = '#8b5cf6'

/** Is most of `b` inside `area`? A line lying flat has no height; it counts by its length. */
function mostlyIn(b: Box, area: Box): boolean {
  const w = Math.max(1, b.w)
  const h = Math.max(1, b.h)
  const ox = Math.max(0, Math.min(b.x + w, area.x + area.w) - Math.max(b.x, area.x))
  const oy = Math.max(0, Math.min(b.y + h, area.y + area.h) - Math.max(b.y, area.y))
  return ox * oy >= 0.5 * w * h
}

/**
 * What a person sees in an area: every item at least half inside it — so a frame the box only cuts across, or the
 * frame the whole area sits in, is not one of them — and every line that joins two of those or lies mostly inside.
 * Items in the order they are painted, then the lines.
 */
export function itemsIn(area: Box, els: Record<string, El>): string[] {
  const order = paintOrder(els)
  const inside = new Set<string>()
  for (const e of order) {
    if (e.t === 'line') continue
    const b = boxOf(e, els)
    if (b && mostlyIn(b, area)) inside.add(e.id)
  }
  const lines: string[] = []
  for (const e of order) {
    if (e.t !== 'line') continue
    const joins = isAttached(e.a) && isAttached(e.b) && inside.has(e.a.id) && inside.has(e.b.id)
    const b = joins ? null : boxOf(e, els)
    if (joins || (b && mostlyIn(b, area))) lines.push(e.id)
  }
  return [...inside, ...lines].slice(0, MAX_AREA_IDS)
}

/** The box two corners of a drag make, in whole pixels. */
export function boxBetween(a: { x: number; y: number }, b: { x: number; y: number }): Box {
  const x = Math.round(Math.min(a.x, b.x))
  const y = Math.round(Math.min(a.y, b.y))
  return { x, y, w: Math.max(1, Math.round(Math.max(a.x, b.x)) - x), h: Math.max(1, Math.round(Math.max(a.y, b.y)) - y) }
}

/** A tap on one item asks about that item: its box, with room round it. */
export function aroundItem(b: Box, pad = 24): Box {
  return { x: Math.round(b.x - pad), y: Math.round(b.y - pad), w: Math.round(b.w + 2 * pad), h: Math.round(b.h + 2 * pad) }
}

/**
 * Where the question's window stands beside the box on the screen: under it when there is room, else above it, else
 * to its right, else over its top — always inside the stage. `box` and `stage` are screen pixels.
 */
export function placeAsk(box: Box, stage: { w: number; h: number }, win: { w: number; h: number }, gap = 12): { left: number; top: number } {
  const clampX = (x: number) => Math.max(8, Math.min(x, stage.w - win.w - 8))
  const clampY = (y: number) => Math.max(8, Math.min(y, stage.h - win.h - 8))
  if (box.y + box.h + gap + win.h <= stage.h - 8) return { left: clampX(box.x), top: box.y + box.h + gap }
  if (box.y - gap - win.h >= 8) return { left: clampX(box.x), top: box.y - gap - win.h }
  if (box.x + box.w + gap + win.w <= stage.w - 8) return { left: box.x + box.w + gap, top: clampY(box.y) }
  return { left: clampX(box.x), top: clampY(box.y) }
}

// ── kept questions: this device's, per board ───────────────────────────────────────────────────────────────────

/** A question the person kept on the board after sending it: where, what was asked, and whether it is folded away. */
export type KeptAsk = Box & { id: string; text: string; ts: number; hidden?: boolean }

const KEPT = 'avg.board.asks.'
const CLEAR = 'avg.board.askClear'
/** How many kept questions a board holds on this device; the oldest goes first. */
const MAX_KEPT = 30

export function loadKept(conv: string, board: string): KeptAsk[] {
  try {
    const raw = JSON.parse(localStorage.getItem(`${KEPT}${conv}.${board}`) ?? '[]') as unknown
    if (!Array.isArray(raw)) return []
    return raw.filter(
      (a): a is KeptAsk =>
        !!a && typeof a === 'object' && typeof a.id === 'string' && typeof a.text === 'string' && [a.x, a.y, a.w, a.h, a.ts].every((n) => typeof n === 'number'),
    )
  } catch {
    return []
  }
}

export function saveKept(conv: string, board: string, list: KeptAsk[]): void {
  try {
    const key = `${KEPT}${conv}.${board}`
    if (!list.length) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(list.slice(-MAX_KEPT)))
  } catch {
    /* a private window: kept until the board closes */
  }
}

/** Does a sent question leave the board by itself? Yes, until the person says otherwise once. */
export function clearsAfterSend(): boolean {
  try {
    return localStorage.getItem(CLEAR) !== 'keep'
  } catch {
    return true
  }
}

export function setClearsAfterSend(clear: boolean): void {
  try {
    if (clear) localStorage.removeItem(CLEAR)
    else localStorage.setItem(CLEAR, 'keep')
  } catch {
    /* the default next time */
  }
}

// ── showing an asked-about area again, from the chat ───────────────────────────────────────────────────────────

/** An area to show on a board: where it is, and what was asked about it. */
export type Shown = { conv: string; board: string; area: Box; text: string }

const SHOW = 'avg-board-area'
let pending: Shown | null = null

/** Show an area on its board: the board takes it when it opens — or at once, when it is open already. */
export function showArea(s: Shown): void {
  pending = s
  window.dispatchEvent(new Event(SHOW))
}

/** The area waiting for this board, if any; it is taken once. */
export function takeArea(conv: string, board: string): Shown | null {
  if (!pending || pending.conv !== conv || pending.board !== board) return null
  const s = pending
  pending = null
  return s
}

export function onShowArea(fn: () => void): () => void {
  window.addEventListener(SHOW, fn)
  return () => window.removeEventListener(SHOW, fn)
}

/** What a message says of an area (chat messages.ts AreaFields), or null: it is about none. */
export type MessageArea = Box & { ids: string[]; shot?: string }

export function messageArea(v: unknown): MessageArea | null {
  if (!v || typeof v !== 'object') return null
  const a = v as Record<string, unknown>
  const n = (k: string) => (typeof a[k] === 'number' && Number.isFinite(a[k]) ? (a[k] as number) : null)
  const [x, y, w, h] = [n('x'), n('y'), n('w'), n('h')]
  if (x === null || y === null || w === null || h === null || w <= 0 || h <= 0) return null
  const ids = Array.isArray(a['ids']) ? (a['ids'] as unknown[]).filter((i): i is string => typeof i === 'string') : []
  return { x, y, w, h, ids, ...(typeof a['shot'] === 'string' ? { shot: a['shot'] } : {}) }
}
